import type { PermissionId } from "@/modules/roles/domain/contracts/permissions";
import { authClient } from "../api/client";
import { useQuery } from "@tanstack/react-query";
import { MainDefaultLegalEntityV1Schema } from "@lifefootball/contracts/identity/main-default-legal-entity";
import { config } from "@/shared/lib/config";

export type AuthUser = {
  id?: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
};

type Nullable<T> = T | null;

export function useAuthState() {
  const { data, isPending, error, refetch } = authClient.useSession();

  const session = (data ?? null) as Nullable<typeof data>;
  const user = (data?.user ?? null) as Nullable<AuthUser>;
  const isAuthenticated = Boolean(user) && !isPending;
  const organization = useQuery({
    queryKey: ["organization", "default-legal-entity", user?.id, data?.session?.id],
    enabled: isAuthenticated, retry: false, staleTime: 0,
    refetchOnMount: "always", refetchOnWindowFocus: "always",
    queryFn: async () => {
      const response = await fetch(`${config.apiUrl}/organization/default-legal-entity`, { credentials: "include", cache: "no-store" });
      const parsed = MainDefaultLegalEntityV1Schema.parse(await response.json());
      if (!response.ok && parsed.state !== "denied" && parsed.state !== "unavailable") throw new Error("MAIN_DEFAULT_LEGAL_ENTITY_UNAVAILABLE");
      return parsed;
    },
  });
  const permissions = (data?.permissions ?? []) as PermissionId[];

  const hasPermission = (permission: PermissionId): boolean => {
    return permissions.includes(permission);
  };

  const hasAnyPermission = (required: PermissionId[]): boolean => {
    if (!required?.length) return true;
    return required.some((p) => permissions.includes(p));
  };

  const hasAllPermissions = (required: PermissionId[]): boolean => {
    if (!required?.length) return true;
    return required.every((p) => permissions.includes(p));
  };

  const signOut = async (): Promise<void> => {
    await authClient.signOut();
  };

  return {
    organizationContext: !organization.isError && !organization.isFetching ? organization.data : undefined,
    session,
    user,
    isAuthenticated,
    isLoading: isPending,
    error,
    signOut,
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refetch,
  };
}

export type UseAuthStateReturn = ReturnType<typeof useAuthState>;
